import { useState } from "react";
import { useTranslation } from "react-i18next";

const PLANS = [
  {
    id: "starter",
    nameKey: "pricing.starter.name",
    tagKey: "pricing.starter.tag",
    descKey: "pricing.starter.desc",
    ctaKey: "pricing.starter.cta",
    color: "#34D399",
    highlighted: false,
    features: [
      "pricing.starter.f1",
      "pricing.starter.f2",
      "pricing.starter.f3",
      "pricing.starter.f4",
      "pricing.starter.f5",
    ],
    missing: ["pricing.starter.m1", "pricing.starter.m2", "pricing.starter.m3"],
  },
  {
    id: "pro",
    nameKey: "pricing.pro.name",
    tagKey: "pricing.pro.tag",
    descKey: "pricing.pro.desc",
    ctaKey: "pricing.pro.cta",
    color: "#00D2A8",
    highlighted: true,
    features: [
      "pricing.pro.f1",
      "pricing.pro.f2",
      "pricing.pro.f3",
      "pricing.pro.f4",
      "pricing.pro.f5",
      "pricing.pro.f6",
    ],
    missing: ["pricing.pro.m1"],
  },
  {
    id: "enterprise",
    nameKey: "pricing.enterprise.name",
    tagKey: "pricing.enterprise.tag",
    descKey: "pricing.enterprise.desc",
    ctaKey: "pricing.enterprise.cta",
    color: "#818CF8",
    highlighted: false,
    features: [
      "pricing.enterprise.f1",
      "pricing.enterprise.f2",
      "pricing.enterprise.f3",
      "pricing.enterprise.f4",
      "pricing.enterprise.f5",
      "pricing.enterprise.f6",
      "pricing.enterprise.f7",
    ],
    missing: [],
  },
];

export default function PricingSection() {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <section
      id="pricing"
      className="py-24"
      style={{ backgroundColor: "var(--bg-base)" }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ color: "var(--accent)" }}
          >
            {t("pricing.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-extrabold mb-4"
            style={{ color: "var(--text-primary)" }}
          >
            {t("pricing.title")}
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan) => {
            const isActive = hovered === plan.id || plan.highlighted;
            return (
              <div
                key={plan.id}
                className="relative rounded-3xl p-8 flex flex-col transition-all duration-300 cursor-default border"
                style={{
                  backgroundColor: plan.highlighted ? `${plan.color}0D` : "var(--bg-surface)",
                  borderColor: isActive ? plan.color : "var(--border-color)",
                  transform: plan.highlighted ? "scale(1.02)" : "scale(1)",
                }}
                onMouseEnter={() => setHovered(plan.id)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Popular badge */}
                {plan.highlighted && (
                  <div
                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap"
                    style={{ backgroundColor: plan.color, color: "#0d1117" }}
                  >
                    {t("pricing.popular")}
                  </div>
                )}

                {/* Plan name + tag */}
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-xl font-extrabold"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {t(plan.nameKey)}
                    </span>
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${plan.color}1A`,
                        color: plan.color,
                      }}
                    >
                      {t(plan.tagKey)}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {t(plan.descKey)}
                  </p>
                </div>

                {/* CTA button */}
                <button
                  className="w-full py-3 rounded-xl text-sm font-semibold mb-7 transition-all cursor-pointer whitespace-nowrap border"
                  style={
                    plan.highlighted
                      ? {
                          backgroundColor: plan.color,
                          borderColor: plan.color,
                          color: "#0d1117",
                        }
                      : {
                          backgroundColor: "transparent",
                          borderColor: plan.color,
                          color: plan.color,
                        }
                  }
                  onMouseEnter={(e) => {
                    if (!plan.highlighted) {
                      (e.currentTarget as HTMLElement).style.backgroundColor = `${plan.color}18`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!plan.highlighted) {
                      (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {t(plan.ctaKey)}
                </button>

                {/* Divider */}
                <div
                  className="w-full h-px mb-6"
                  style={{ backgroundColor: "var(--border-color)" }}
                />

                {/* Included features */}
                <div className="flex-1 space-y-3">
                  <p
                    className="text-xs font-semibold uppercase tracking-wider mb-3"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {t("pricing.includes")}
                  </p>
                  {plan.features.map((fk) => (
                    <div key={fk} className="flex items-start gap-2.5">
                      <div
                        className="w-4 h-4 flex items-center justify-center rounded-full shrink-0 mt-0.5"
                        style={{ backgroundColor: `${plan.color}20` }}
                      >
                        <i className="ri-check-line text-xs" style={{ color: plan.color }} />
                      </div>
                      <span
                        className="text-sm leading-relaxed"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {t(fk)}
                      </span>
                    </div>
                  ))}

                  {/* Not included */}
                  {plan.missing.length > 0 && (
                    <>
                      <div className="pt-1" />
                      {plan.missing.map((mk) => (
                        <div key={mk} className="flex items-start gap-2.5 opacity-40">
                          <div
                            className="w-4 h-4 flex items-center justify-center rounded-full shrink-0 mt-0.5"
                            style={{ backgroundColor: "var(--border-color)" }}
                          >
                            <i className="ri-close-line text-xs" style={{ color: "var(--text-muted)" }} />
                          </div>
                          <span
                            className="text-sm leading-relaxed line-through"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {t(mk)}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <p
          className="text-center text-sm mt-10"
          style={{ color: "var(--text-muted)" }}
        >
          {t("pricing.note")}
        </p>
      </div>
    </section>
  );
}